import { Button } from "@aix/ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import z from "zod"

import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"
import { api } from "~/utils/api"

const metaCallbackSearchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
  accountKey: z.string().optional(),
})

const metaOauthStateSchema = z.object({
  source: z.literal("admin-ads-performance").optional(),
  accountKey: z.string().optional(),
  selectedPlatformAccountIds: z.array(z.string()).optional(),
})

function getRedirectUri() {
  return `${window.location.origin}/admin/ads-performance/meta-callback`
}

function decodeMetaOauthState(state: string | undefined): z.infer<typeof metaOauthStateSchema> {
  if (!state?.startsWith("ads-performance:")) return {}
  try {
    const encoded = state.slice("ads-performance:".length)
    const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=").replace(/-/g, "+").replace(/_/g, "/")
    return metaOauthStateSchema.parse(JSON.parse(window.atob(padded)))
  } catch {
    return {}
  }
}

function MetaAdsCallbackPage() {
  const navigate = useNavigate({ from: "/admin/ads-performance/meta-callback" })
  const search = Route.useSearch()
  const [hasCompleted, setHasCompleted] = useState(false)
  const redirectUri = useMemo(() => getRedirectUri(), [])
  const oauthState = useMemo(() => decodeMetaOauthState(search.state), [search.state])
  const completeMetaOAuth = api.adsPerformance.oauth.meta.complete.useMutation()

  useEffect(() => {
    if (hasCompleted || !search.code || search.error) return
    setHasCompleted(true)
    completeMetaOAuth.mutate({
      code: search.code,
      state: search.state,
      redirectUri,
      accountKey: search.accountKey ?? oauthState.accountKey,
      selectedPlatformAccountIds: oauthState.selectedPlatformAccountIds,
    })
  }, [completeMetaOAuth, hasCompleted, oauthState.accountKey, oauthState.selectedPlatformAccountIds, redirectUri, search.accountKey, search.code, search.error, search.state])

  const returnToAdsPerformance = () => {
    void navigate({ to: "/admin/ads-performance", search: { view: "inventory" } })
  }

  const errorMessage =
    search.error_description ??
    search.error ??
    (completeMetaOAuth.error instanceof Error ? completeMetaOAuth.error.message : null)

  return (
    <SuperAdminLayout>
      <CustomHead title="Meta Ads Callback | VidMax Admin" />
      <div className="mx-auto flex min-h-[60vh] max-w-2xl items-center px-6 py-10">
        <div className="w-full rounded-md border border-zinc-200 bg-white p-6">
          <div className="flex items-start gap-3">
            {errorMessage ? (
              <AlertTriangle className="mt-1 h-5 w-5 text-red-600" />
            ) : completeMetaOAuth.isSuccess ? (
              <CheckCircle2 className="mt-1 h-5 w-5 text-emerald-600" />
            ) : (
              <Loader2 className="mt-1 h-5 w-5 animate-spin text-zinc-500" />
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-zinc-950">
                {errorMessage ? "Meta connection failed" : completeMetaOAuth.isSuccess ? "Meta connection complete" : "Connecting Meta Ads"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {errorMessage
                  ? errorMessage
                  : completeMetaOAuth.isSuccess
                    ? "The OAuth token was encrypted and accessible ad accounts were linked to this team."
                    : "Exchanging the Meta authorization code and storing the connection."}
              </p>

              {completeMetaOAuth.data ? (
                <div className="mt-5 rounded-md border border-zinc-100 bg-zinc-50 p-4 text-sm">
                  <div className="font-medium text-zinc-950">Linked accounts</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {completeMetaOAuth.data.linkedAccounts.length} linked from {completeMetaOAuth.data.accessibleAccounts.length} accessible.
                  </div>
                  {oauthState.selectedPlatformAccountIds && oauthState.selectedPlatformAccountIds.length > 0 ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Requested {oauthState.selectedPlatformAccountIds.length} selected account IDs.
                    </div>
                  ) : null}
                  <div className="mt-3 grid gap-2">
                    {completeMetaOAuth.data.accessibleAccounts.map((account) => (
                      <div key={account.id} className="rounded-md border border-zinc-200 bg-white px-3 py-2">
                        <div className="text-xs font-medium text-zinc-950">{account.name ?? account.id}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {account.id}
                          {account.currency ? ` · ${account.currency}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {!search.code && !search.error ? (
                <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Missing Meta authorization code. Start from the Ads Performance connection panel.
                </div>
              ) : null}

              <div className="mt-6 flex flex-wrap gap-2">
                <Button type="button" onClick={returnToAdsPerformance}>
                  Back to Ads Performance
                </Button>
                {errorMessage ? (
                  <Button type="button" variant="outline" onClick={returnToAdsPerformance}>
                    Retry from Connections
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  )
}

export const Route = createFileRoute("/admin/ads-performance/meta-callback")({
  validateSearch: metaCallbackSearchSchema,
  component: MetaAdsCallbackPage,
})
